// Lowercase to match the backend DynamicDataModelFieldType `.value` strings.
export type PayloadFieldType = 'text' | 'date' | 'enum';

export const PAYLOAD_FIELD_TYPES: PayloadFieldType[] = ['text', 'date', 'enum'];

export const PAYLOAD_FIELD_TYPE_LABELS: Record<PayloadFieldType, string> = {
  text: 'Text',
  date: 'Date',
  enum: 'Enum',
};

// Wire contract (subset of the shared dynamic-data field configuration used by the API).
export interface PayloadField {
  key: string;
  field_type: PayloadFieldType;
  title: string;
  description: string;
  is_required: boolean;
  enum_values?: string[]; // present only when field_type === 'enum'
}

// Code-defined per tool by the backend registry, never operator-settable.
export type ExternalToolHttpMethod = 'GET' | 'POST';

// GET/POST/PUT response shape. Extra-header *values* are never returned (encrypted at rest,
// exposed only to the tool-call proxy), so the response can at most say *whether* any are
// configured via `has_extra_headers` — never the values themselves.
export interface ExternalToolConfig {
  id: number;
  tool_name: string;
  tool_title: string;
  url: string;
  payload_fields: PayloadField[];
  // `{param name the endpoint expects: which FiveSigma-supplied value fills it}`. Unlike extra
  // headers these ARE echoed on read paths — they travel in the request line and are not a secret.
  query_params: Record<string, string>;
  is_enabled: boolean;
  sub_organization_id: number | null;
  // Whether any extra headers are stored. Optional: older backends omit it (there is no value to
  // read), in which case the "configured" indicator simply isn't shown.
  has_extra_headers?: boolean;
}

export interface AvailableTool {
  tool_name: string;
  tool_title: string;
  // Required (unlike response_json_schema): it decides whether payload fields apply at all, and a
  // guess would offer a request body for a GET tool.
  http_method: ExternalToolHttpMethod;
  // The FiveSigma-supplied values this tool's query params may be bound to. Server-supplied so the
  // value dropdown can never offer a source the backend would reject; optional (defaulted to []) so
  // an older backend that omits it just offers nothing rather than crashing the editor.
  query_param_sources?: string[];
  // Read-only, code-defined by FiveSigma — the exact JSON Schema the endpoint must return (and the
  // proxy validates against). Optional so an older backend that omits it simply hides the panel;
  // nullable because the backend field is `Optional[...] = None` and can serialize `null` (matches
  // the generated wire contract in server_shared/openapi/data-contracts.ts).
  response_json_schema?: Record<string, unknown> | null;
  response_contract_version?: string;
}

// Extra headers replace the entire stored set — there is no per-key merge. So the wire field is
// tri-state: omitted (leave the stored value untouched), a full map (replace with exactly this
// set), or null (clear). See ExtraHeadersMode / buildExtraHeadersRequest.
export type ExtraHeadersRequestField = Record<string, string> | null;

export interface CreateExternalToolConfigRequest {
  tool_name: string;
  url: string;
  payload_fields: PayloadField[];
  query_params: Record<string, string>;
  is_enabled: boolean;
  // FE v1 is org-level and omits this; carried for contract fidelity.
  sub_organization_id?: number | null;
  extra_headers?: ExtraHeadersRequestField;
}

export interface UpdateExternalToolConfigRequest {
  url: string;
  payload_fields: PayloadField[];
  // Required on update, like `payload_fields`: the backend echoes the stored map on every read path,
  // so the client always round-trips it and an omission is a 400 rather than a silent wipe.
  query_params: Record<string, string>;
  is_enabled: boolean;
  sub_organization_id?: number | null;
  extra_headers?: ExtraHeadersRequestField;
}

// ---- Formik form state ----
export interface PayloadFieldForm {
  key: string;
  field_type: PayloadFieldType;
  title: string;
  description: string;
  is_required: boolean;
  enum_values: string[]; // always an array in the form; trimmed to [] for non-enum on submit
}

// Three-state selector for extra headers (see ExtraHeadersRequestField):
//   'keep'    -> leave the stored headers untouched (omit the field)
//   'replace' -> send the full set the operator entered (rows below)
//   'clear'   -> remove all stored headers (send null)
export type ExtraHeadersMode = 'keep' | 'replace' | 'clear';

export interface ExtraHeaderRow {
  name: string;
  value: string;
}

export interface QueryParamRow {
  // The param name the customer's endpoint expects — theirs to choose.
  name: string;
  // Which FiveSigma-supplied value fills it on every call (one of the tool's `query_param_sources`),
  // NOT a literal: the backend resolves it per call from the claim Clive is working on.
  value: string;
}

export interface ConfigFormValues {
  tool_name: string;
  url: string;
  payload_fields: PayloadFieldForm[];
  // A plain field, not an extra_headers-style keep/replace/clear tri-state: that only exists because
  // header values are never returned, so there is nothing to pre-fill. Query params are returned, so
  // the ordinary round-trip works.
  query_params: QueryParamRow[];
  is_enabled: boolean;
  extra_headers_mode: ExtraHeadersMode;
  extra_headers: ExtraHeaderRow[];
}

export const emptyPayloadField = (): PayloadFieldForm => ({
  key: '',
  field_type: 'text',
  title: '',
  description: '',
  is_required: false,
  enum_values: [],
});

export const emptyHeaderRow = (): ExtraHeaderRow => ({ name: '', value: '' });

export const emptyQueryParamRow = (): QueryParamRow => ({ name: '', value: '' });

// Human labels for the value sources the server offers per tool (`AvailableTool.query_param_sources`).
// Labels only — the options themselves always come from the server, so a source added or removed
// backend-side needs no change here beyond a nicer label; unknown keys fall back to the raw key.
export const QUERY_PARAM_SOURCE_LABELS: Record<string, string> = {
  claim_number: 'Claim number',
  sub_account_id: 'Sub-account ID',
};

export const configToFormValues = (config: ExternalToolConfig | null): ConfigFormValues => ({
  tool_name: config?.tool_name ?? '',
  url: config?.url ?? '',
  payload_fields: (config?.payload_fields ?? []).map((field) => ({
    key: field.key,
    field_type: field.field_type,
    title: field.title,
    description: field.description,
    is_required: field.is_required,
    enum_values: field.enum_values ?? [],
  })),
  query_params: Object.entries(config?.query_params ?? {}).map(([name, value]) => ({ name, value })),
  is_enabled: config?.is_enabled ?? true,
  // Default to 'keep': never touch stored headers unless the operator explicitly changes this.
  // Existing values are never returned by the API, so there is nothing to pre-fill.
  extra_headers_mode: 'keep',
  extra_headers: [],
});

const toPayloadField = (field: PayloadFieldForm): PayloadField => ({
  key: field.key.trim(),
  field_type: field.field_type,
  title: field.title.trim(),
  description: field.description.trim(),
  is_required: field.is_required,
  // enum_values only meaningful for enum; drop it otherwise so we never ship stale values.
  ...(field.field_type === 'enum'
    ? { enum_values: field.enum_values.map((value) => value.trim()).filter(Boolean) }
    : {}),
});

// Translate the 3-state extra-headers control into the wire field. Returns an empty object for
// 'keep' so spreading it omits `extra_headers` entirely (vs. a null, which means "clear").
const buildExtraHeadersRequest = (values: ConfigFormValues): { extra_headers?: ExtraHeadersRequestField } => {
  if (values.extra_headers_mode === 'clear') return { extra_headers: null };
  if (values.extra_headers_mode === 'replace') {
    const entries = values.extra_headers
      .map((row): [string, string] => [row.name.trim(), row.value])
      .filter(([name]) => name !== '');
    // Replacing with no named rows means "no headers". Send null (clear) rather than an empty map:
    // the backend stores an explicit `{}` as an (empty) header set distinct from cleared, so an
    // empty map would leave the config looking configured when the operator meant to remove them.
    return { extra_headers: entries.length === 0 ? null : Object.fromEntries(entries) };
  }
  return {}; // 'keep' — leave the stored headers untouched
};

// A half-filled row is dropped: a name with no source has no value to send (the backend rejects an
// unbound param), and a source with no name has nowhere to go. The form's row-completeness test
// already blocks the cases where dropping one would lose what the operator typed.
const buildQueryParams = (values: ConfigFormValues): Record<string, string> =>
  Object.fromEntries(
    values.query_params
      .map((row): [string, string] => [row.name.trim(), row.value])
      .filter(([name, source]) => name !== '' && source !== '')
  );

export const formValuesToCreateRequest = (values: ConfigFormValues): CreateExternalToolConfigRequest => ({
  tool_name: values.tool_name,
  url: values.url.trim(),
  payload_fields: values.payload_fields.map(toPayloadField),
  query_params: buildQueryParams(values),
  is_enabled: values.is_enabled,
  ...buildExtraHeadersRequest(values),
});

export const formValuesToUpdateRequest = (values: ConfigFormValues): UpdateExternalToolConfigRequest => ({
  url: values.url.trim(),
  payload_fields: values.payload_fields.map(toPayloadField),
  query_params: buildQueryParams(values),
  is_enabled: values.is_enabled,
  ...buildExtraHeadersRequest(values),
});
